import { generateGuid, getTimestamp } from '@bingads-webui-universal/primitive-utilities';
import _ from 'underscore';

export function getDefaultUrl(serverContext) {
  const { CmAPI } = serverContext.ServicesEndpoints;
  return CmAPI;
}

const getAjaxStubLogger = _.memoize((instrumentation) => {
  if (instrumentation && instrumentation.ajaxStubScenario) {
    const scenario = instrumentation.ajaxStubScenario.create();
    const activity = scenario.ajaxStubActivity.create();
    return activity;
  }

  return {
    ajax: _.noop,
    trace: _.noop,
    error: _.noop,
  };
});

const getRequestPayloadLogger = _.memoize((instrumentation) => {
  const schema = {
    scenario: [
      {
        name: 'CampaignFetch',
        value: 'CampaignFetch',
        activity: [
          { name: 'RequestPayload', value: 'RequestPayload' },
          { name: 'Response', value: 'Response' },
        ],
      },
    ],
  };

  instrumentation.addScenario(schema);

  const scenario = instrumentation.CampaignFetch.create();
  const requestLogger = scenario.RequestPayload.create();
  const responseLogger = scenario.Response.create();

  return {
    requestLogger,
    responseLogger,
  };
});

export function getDefaultOptions(serverContext, instrumentation) {
  const { Id: aid, AdvertiserCustomerID: cid } = serverContext.CurrentAccount;
  const { CmAPI } = serverContext.ServicesEndpoints;
  const isOnebox = CmAPI.indexOf('redmond.corp') > -1;
  const startTimes = {};
  const httpMethods = {};

  const ajaxStubLogger = getAjaxStubLogger(instrumentation);
  const { requestLogger } = getRequestPayloadLogger(instrumentation);

  function beforeFetch(options, url, route) {
    const httpMethod = options.method || 'GET';
    const requestId = options.headers['x-ms-requestid'];
    startTimes[requestId] = getTimestamp();
    httpMethods[requestId] = httpMethod;

    const path = `${url}/${route}`;
    requestLogger.trace({ body: options.body }, path, requestId, httpMethod);

    // Mimic AjaxEventHandler::beforeSendHandler
    ajaxStubLogger.ajax(
      requestId,
      path,
      true,
      httpMethod,
      0,
      true,
      '',
      -1
    );
  }

  function afterFetch(fetchResponse) {
    const requestId = fetchResponse.headers.get('x-ms-requestid');
    const responseUrl = new URL(fetchResponse.url);

    const startTime = startTimes[requestId];
    const duration = startTime ? getTimestamp() - startTime : 0;
    delete startTimes[requestId];

    const httpMethod = httpMethods[requestId];
    delete httpMethods[requestId];

    // Mimic AjaxEventHandler::successHandler
    ajaxStubLogger.ajax(
      requestId,
      responseUrl && responseUrl.pathname,
      false,
      httpMethod,
      duration,
      fetchResponse.ok,
      '',
      -1
    );

    // Mimic AjaxEventHandler::errorHandler
    if (!fetchResponse.ok) {
      const error = _.compact([
        `Ajax error [${fetchResponse.status}]`,
        fetchResponse.statusText || 'error',
        fetchResponse.bodyText,
      ]).join(', ');

      ajaxStubLogger.error(error, responseUrl && responseUrl.pathname, requestId, '', httpMethod);
    }
  }

  const finalOptions = {
    headers: {
      'Content-Type': 'application/json',
      lcid: serverContext.CurrentUser.Locale,
      'x-ms-clientapplication': 'bingadsweb',
      'x-ms-applicationname': 'bingadsweb',
      'x-ms-pagetrackingid': serverContext.PageTrackingId,
      Authorization: `CCMTSmallToken ${serverContext.Token}`,
      CustomerAccountId: aid,
      CustomerId: cid,
      ...(isOnebox? { DeveloperToken: '1L9L5BYH550' }: {}),
    },
    cachePolicy: 'cache-first',
  };

  const defaultOptions = {
    ...finalOptions,
    interceptors: {
      request: ({ route, url, options }) => {
        const reqId = generateGuid();
        // eslint-disable-next-line no-param-reassign
        options.headers['x-ms-requestid'] = reqId;
        // eslint-disable-next-line no-param-reassign
        options.headers['Request-Id'] = reqId;
        // eslint-disable-next-line no-param-reassign
        options.headers['x-ms-trackingid'] = reqId;
        beforeFetch(options, url, route);
        return options;
      },
      response: ({ response }) => {
        afterFetch(response);
        return response;
      },
    },
  };

  return defaultOptions;
}
